import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Bell,
  CreditCard,
  Send,
  MoreVertical,
  Search,
} from "lucide-react";
import { financeAPI } from "../api/axiosClient";
import { SchoolReceivableDetail, SchoolReceivableStudent } from "../api/types";
import { toast } from "react-hot-toast";

interface ReceivableDetailViewProps {
  receivableId: string;
  onBack: () => void;
}

// Payment type mapping
const getPaymentTypeLabel = (type: number): string => {
  switch (type) {
    case 0:
      return "Etkinlik Ücreti";
    case 1:
      return "Servis Ücreti";
    case 2:
      return "Yemek Ücreti";
    case 3:
      return "Kitap Ücreti";
    case 4:
      return "Kayıt Ücreti";
    default:
      return "Diğer";
  }
};


// Currency helper
const getCurrencySymbol = (currency: string | number): string => {
  if (typeof currency === "number") {
    switch (currency) {
      case 0:
        return "TRY";
      default:
        return "TRY";
    }
  }
  switch (currency) {
    case "TRY":
      return "TRY";
    default:
      return currency;
  }
};

const formatPrice = (price: number, currency: string | number): string => {
  const symbol = getCurrencySymbol(currency);
  return `${price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Status helper for student payments
const getStudentStatusInfo = (
  status: number,
  statusDesc: string,
): { label: string; className: string; icon: React.ElementType } => {
  switch (status) {
    case 0:
      return {
        label: statusDesc || "Ödeme Bekleniyor",
        className: "bg-yellow-100 text-yellow-800",
        icon: Clock,
      };
    case 1:
      return {
        label: statusDesc || "Kısmi Ödeme",
        className: "bg-blue-100 text-blue-800",
        icon: AlertCircle,
      };
    case 2:
      return {
        label: statusDesc || "Ödendi",
        className: "bg-green-100 text-green-800",
        icon: CheckCircle,
      };
    case 3:
      return {
        label: statusDesc || "İptal Edildi",
        className: "bg-red-100 text-red-800",
        icon: XCircle,
      };
    default:
      return {
        label: statusDesc || "Bilinmiyor",
        className: "bg-gray-100 text-gray-800",
        icon: AlertCircle,
      };
  }
};

const ReceivableDetailView: React.FC<ReceivableDetailViewProps> = ({
  receivableId,
  onBack,
}) => {
  const [receivable, setReceivable] = useState<SchoolReceivableDetail | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [showReminderModal, setShowReminderModal] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] =
    useState<SchoolReceivableStudent | null>(null);
  const [notificationChannels, setNotificationChannels] = useState({
    sms: false,
    push: false,
    email: false,
  });
  const [sendingReminder, setSendingReminder] = useState<boolean>(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showBulkReminderModal, setShowBulkReminderModal] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const fetchReceivableDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await financeAPI.getSchoolReceivableDetail(receivableId);
      if (response.success && response.data) {
        setReceivable(response.data as SchoolReceivableDetail);
      }
    } catch (error) {
      console.error("Error fetching receivable details:", error);
      toast.error("Odeme istegi detaylari yuklenirken bir hata olustu");
    } finally {
      setLoading(false);
    }
  }, [receivableId]);

  useEffect(() => {
    fetchReceivableDetails();
  }, [fetchReceivableDetails]);

  // Reminder functions
  const handleOpenReminderModal = (student: SchoolReceivableStudent) => {
    setSelectedStudent(student);
    setShowReminderModal(true);
    setNotificationChannels({ sms: false, push: false, email: false });
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  // Toggle dropdown
  const handleToggleDropdown = (
    studentEventId: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (openDropdownId === studentEventId) {
      setOpenDropdownId(null);
      setDropdownPosition(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right - 192 + window.scrollX, // 192px is dropdown width
      });
      setOpenDropdownId(studentEventId);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
      setDropdownPosition(null);
    };
    if (openDropdownId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openDropdownId]);

  // Handle bulk reminder
  const handleBulkReminder = () => {
    setShowBulkReminderModal(true);
    setNotificationChannels({ sms: false, push: false, email: false });
  };

  // Send bulk reminder
  const handleSendBulkReminder = async () => {
    if (
      !notificationChannels.sms &&
      !notificationChannels.push &&
      !notificationChannels.email
    ) {
      toast.error("Lutfen en az bir bildirim kanali secin");
      return;
    }

    if (!receivable) {
      toast.error("Ödeme bilgisi bulunamadı");
      return;
    }

    // Get unpaid students
    const unpaidStudents =
      receivable.student?.filter((s) => s.status !== 2) || [];
    if (unpaidStudents.length === 0) {
      toast.error("Bildirim gönderilecek öğrenci bulunamadı");
      return;
    }

    const studentIds = receivable.student.map(x => x.id)

    setSendingReminder(true);
    try {
      const response = await financeAPI.sendSchoolReceivableReminder({
        id: receivable.id,
        receivableId: studentIds,
        sms: notificationChannels.sms,
        push: notificationChannels.push,
        email: notificationChannels.email,
        isAllUser: true,
      });

      if (response.success) {
        toast.success(`${unpaidStudents.length} öğrenciye bildirim gönderildi`);
        setShowBulkReminderModal(false);
        setNotificationChannels({ sms: false, push: false, email: false });
      } else {
        toast.error(
          response.message || "Bildirim gönderilirken bir hata oluştu",
        );
      }
    } catch (error: any) {
      console.error("Error sending bulk reminder:", error);
    } finally {
      setSendingReminder(false);
    }
  };

  // Filter students by search term
  const filteredStudents =
    receivable?.student?.filter(
      (student) =>
        student.student.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.user.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const handleChannelChange = (
    channel: "sms" | "push" | "email",
    checked: boolean,
  ) => {
    setNotificationChannels((prev) => ({
      ...prev,
      [channel]: checked,
    }));
  };

  const handleSendReminder = async () => {
    if (
      !notificationChannels.sms &&
      !notificationChannels.push &&
      !notificationChannels.email
    ) {
      toast.error("Lütfen en az bir bildirim kanalı seçin");
      return;
    }

    if (!selectedStudent || !receivable) {
      toast.error("Öğrenci bilgisi bulunamadı");
      return;
    }

    setSendingReminder(true);
    try {
      const response = await financeAPI.sendSchoolReceivableReminder({
        id: receivable.id,
        receivableId: [selectedStudent.id],
        sms: notificationChannels.sms,
        push: notificationChannels.push,
        email: notificationChannels.email,
        isAllUser: false,
      });

      if (response.success) {
        toast.success("Bildirim basariyla gonderildi");
        setShowReminderModal(false);
        setSelectedStudent(null);
        setNotificationChannels({ sms: false, push: false, email: false });
      } else {
        toast.error(
          response.message || "Bildirim gonderilirken bir hata olustu",
        );
      }
    } catch (error: any) {
      console.error("Error sending reminder:", error);
    } finally {
      setSendingReminder(false);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!receivable?.student) return { total: 0, pending: 0, paid: 0 };

    let total = 0;
    let pending = 0;
    let paid = 0;

    receivable.student.forEach((s) => {
      total += s.amount;
      if (s.status === 2) {
        paid += s.amount;
      } else {
        pending += s.amount;
      }
    });

    return { total, pending, paid };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B51E0] mx-auto mb-4"></div>
          <p className="text-gray-500">Ödeme isteği detayları yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!receivable) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Geri Dön
        </Button>
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-gray-500">Ödeme isteği bulunamadı</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Geri Dön
        </Button>
      </div>

      {/* Main Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 rounded-xl bg-[#9B51E0] flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">{receivable.name}</CardTitle>
                <CardDescription className="mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {getPaymentTypeLabel(receivable.paymentType)}
                  </span>
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Toplam Tutar */}
            <div className="flex items-start space-x-3">
              <div className="px-4 py-2 bg-green-100 rounded-lg">
                <span className="w-5 h-5 text-green-600">₺</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam Tutar</p>
                <p className="font-medium text-lg">
                  {formatPrice(totals.total, receivable.currency)}
                </p>
              </div>
            </div>

            {/* Tahsil Edilen */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tahsil Edilen</p>
                <p className="font-medium text-lg text-green-600">
                  {formatPrice(totals.paid, receivable.currency)}
                </p>
              </div>
            </div>

            {/* Bekleyen */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Bekleyen</p>
                <p className="font-medium text-lg text-orange-600">
                  {formatPrice(totals.pending, receivable.currency)}
                </p>
              </div>
            </div>

            {/* Ogrenci Sayisi */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Öğrenci Sayısı</p>
                <p className="font-medium text-lg">
                  {receivable.student?.length || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Donem */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Dönem</p>
                <p className="font-medium">
                  {receivable.period ? formatDate(receivable.period) : "-"}
                </p>
              </div>
            </div>

            {/* Son Odeme Tarihi */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Calendar className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Son Ödeme Tarihi</p>
                <p className="font-medium">
                  {receivable.lastPaymentDate
                    ? formatDateTime(receivable.lastPaymentDate)
                    : "-"}
                </p>
              </div>
            </div>

            {/* Olusturulma Tarihi */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Oluşturulma Tarihi</p>
                <p className="font-medium">
                  {formatDateTime(receivable.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {receivable.description && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-medium text-gray-500 mb-2">
                Aciklama
              </h4>
              <p className="text-gray-700">{receivable.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Students Card */}
      {receivable.student && receivable.student.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Öğrenci Ödeme Durumu
                </CardTitle>
                <CardDescription>
                  Bu ödeme talebine dahil olan öğrenciler
                </CardDescription>
              </div>
              {filteredStudents.filter((s) => s.status !== 2).length > 0 && (
                <Button
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                  onClick={handleBulkReminder}
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Herkese Bildirim Gonder
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Öğrenci ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>

            <div className="overflow-visible">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ogrenci
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kullanici
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tutar
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Islemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => {
                    const statusInfo = getStudentStatusInfo(
                      student.status,
                      student.statusDesc,
                    );
                    const StatusIcon = statusInfo.icon;

                    return (
                      <tr
                        key={student.studentEventId}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-[#9B51E0] flex items-center justify-center">
                              <span className="text-white font-semibold">
                                {student.student.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {student.student}
                              </p>
                              <p className="text-xs text-gray-500">
                                ID: {student.studentId.substring(0, 8)}...
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-900">
                            {student.user}
                          </p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">
                            {formatPrice(student.amount, student.currency)}
                          </p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-left">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleDropdown(student.studentEventId, e);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                          {openDropdownId === student.studentEventId &&
                            dropdownPosition &&
                            ReactDOM.createPortal(
                              <div
                                className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
                                style={{
                                  width: "192px",
                                  top: dropdownPosition.top,
                                  left: dropdownPosition.left,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {student.status !== 2 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenReminderModal(student);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                  >
                                    <Bell className="w-4 h-4 mr-2" />
                                    Bildirim Gönder
                                  </button>
                                )}
                              </div>,
                              document.body,
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reminder Modal */}
      {showReminderModal && selectedStudent && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Bildirim Gonder
                </h3>
              </div>

              <p className="text-gray-600 mb-4">
                <span className="font-medium">{selectedStudent.student}</span>{" "}
                öğrencisinin velilerine bildirim gonderilecek.
              </p>

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600">
                  Tutar:{" "}
                  <span className="font-semibold">
                    {formatPrice(
                      selectedStudent.amount,
                      selectedStudent.currency,
                    )}
                  </span>
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700">
                  Bildirim Kanalları
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sms"
                      checked={notificationChannels.sms}
                      onCheckedChange={(checked: boolean) =>
                        handleChannelChange("sms", checked)
                      }
                    />
                    <Label htmlFor="sms" className="text-sm text-gray-600">
                      SMS
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="push"
                      checked={notificationChannels.push}
                      onCheckedChange={(checked: boolean) =>
                        handleChannelChange("push", checked)
                      }
                    />
                    <Label htmlFor="push" className="text-sm text-gray-600">
                      Uygulama İçi Bildirim
                    </Label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReminderModal(false);
                    setSelectedStudent(null);
                    setNotificationChannels({
                      sms: false,
                      push: false,
                      email: false,
                    });
                  }}
                  disabled={sendingReminder}
                >
                  Iptal
                </Button>
                <Button
                  onClick={handleSendReminder}
                  disabled={sendingReminder}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                >
                  {sendingReminder ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reminder Modal */}
      {showBulkReminderModal && receivable && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Herkese Bildirim Gönder
                </h3>
              </div>

              <p className="text-gray-600 mb-4">
                Odemesi bekleyen{" "}
                <span className="font-medium">
                  {filteredStudents.filter((s) => s.status !== 2).length}{" "}
                  Öğrenci'nin
                </span>{" "}
                velilerine bildirim gonderilecek.
              </p>

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600">
                  Ödeme Talebi:{" "}
                  <span className="font-semibold">{receivable.name}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Toplam Bekleyen:{" "}
                  <span className="font-semibold">
                    {formatPrice(totals.pending, receivable.currency)}
                  </span>
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700">
                  Bildirim Kanalları
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="bulk-sms"
                      checked={notificationChannels.sms}
                      onCheckedChange={(checked: boolean) =>
                        handleChannelChange("sms", checked)
                      }
                    />
                    <Label htmlFor="bulk-sms" className="text-sm text-gray-600">
                      SMS
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="bulk-push"
                      checked={notificationChannels.push}
                      onCheckedChange={(checked: boolean) =>
                        handleChannelChange("push", checked)
                      }
                    />
                    <Label
                      htmlFor="bulk-push"
                      className="text-sm text-gray-600"
                    >
                      Uygulama İçi Bildirim
                    </Label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkReminderModal(false);
                    setNotificationChannels({
                      sms: false,
                      push: false,
                      email: false,
                    });
                  }}
                  disabled={sendingReminder}
                >
                  Iptal
                </Button>
                <Button
                  onClick={handleSendBulkReminder}
                  disabled={sendingReminder}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                >
                  {sendingReminder ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Herkese Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivableDetailView;
